import { useEffect, useRef, useState } from 'react';
import type { ContactTypeRow } from '@ehr/shared';
import { api, apiErrorText } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';
import { ContactTypeFieldForm } from './ContactTypeFieldForm.js';

/**
 * Contact Types manager (V34 Settings → Contact Types, renderTypedManager).
 * An ordered, drag-reorderable list of the account's contact types: each with an
 * active toggle, a system tag (name-locked), a field-count tag, and pencil/delete
 * actions. The pencil opens the per-type field designer. 1:1 with the mockup's
 * .modal-wide / .ctm-* markup; CRUD against /contact-types.
 */

const DESC =
  'The kinds of people on an account. Add your own, edit each type’s fields, and drag to reorder. Toggle a type active/inactive — inactive types stay on existing records but drop off the picker. System types can be deleted but not renamed.';
const HANDLE = '⁙⁙';

const Pencil = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const Xmark = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function ContactTypesManager({ onClose }: { onClose: () => void }) {
  const [types, setTypes] = useState<ContactTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [editType, setEditType] = useState<ContactTypeRow | null>(null);
  const dragKey = useRef<string | null>(null);

  const load = (cb?: (rows: ContactTypeRow[]) => void) => {
    api
      .get<ContactTypeRow[]>('/contact-types')
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setTypes(list);
        setErr(null);
        cb?.(list);
      })
      .catch((e) => setErr(apiErrorText(e, 'Could not load contact types. Please try again.')))
      .finally(() => setLoading(false));
  };
  useEffect(() => load(), []);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const addType = () => {
    // Unique default name so the (orgId,name) constraint never rejects the add.
    const existing = new Set(types.map((t) => t.name.toLowerCase()));
    let name = 'New Contact Type';
    for (let i = 2; existing.has(name.toLowerCase()); i++) name = `New Contact Type ${i}`;
    api
      .post<ContactTypeRow>('/contact-types', { name })
      .then((row) => load(() => setEditType({ ...row, fieldCount: 0 })))
      // ehr#279: say why (signed out, not allowed, a name taken), not just "could not".
      .catch((e) => setErr(apiErrorText(e, 'Could not add a contact type.')));
  };

  const toggleActive = (t: ContactTypeRow) => {
    setTypes((ts) => ts.map((x) => (x.id === t.id ? { ...x, active: !x.active } : x)));
    api.patch(`/contact-types/${t.id}`, { active: !t.active }).catch((e) => {
      setErr(apiErrorText(e, 'Could not change active state.'));
      load();
    });
  };

  const remove = async (t: ContactTypeRow) => {
    if (!(await askConfirm(`Delete “${t.name}”?`))) return;
    api
      .del(`/contact-types/${t.id}`)
      .then(() => load())
      .catch((e) => setErr(apiErrorText(e, 'Could not delete — it may be in use.')));
  };

  const reorder = (targetKey: string) => {
    const dKey = dragKey.current;
    dragKey.current = null;
    if (!dKey || dKey === targetKey) return;
    const next = types.slice();
    const di = next.findIndex((t) => t.id === dKey);
    if (di < 0) return;
    const [moved] = next.splice(di, 1);
    const ti = next.findIndex((t) => t.id === targetKey);
    next.splice(ti < 0 ? next.length : ti, 0, moved!);
    setTypes(next);
    api.patch('/contact-types/reorder', { ids: next.map((t) => t.id) }).catch((e) => {
      setErr(apiErrorText(e, 'Could not reorder.'));
      load();
    });
  };

  const ql = q.trim().toLowerCase();
  const shown = ql ? types.filter((t) => t.name.toLowerCase().includes(ql)) : types;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="modal-lg modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label="Contact Types"
      >
        <div className="modal-head">
          <div>
            <h2>Contact Types</h2>
            <p>{DESC}</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body tr-modal">
          <div className="ctm-toolbar">
            <input
              type="text"
              className="ctm-search-input"
              placeholder="Search contact types…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className="btn primary" onClick={addType}>
              + Add Contact Type
            </button>
          </div>

          {err && <div className="config-err">{err}</div>}

          {loading ? (
            <p className="muted">Loading…</p>
          ) : types.length === 0 ? (
            <div className="ctm-empty">No contact types yet. Click “+ Add Contact Type” above.</div>
          ) : shown.length === 0 ? (
            <div className="ctm-noresults">No matches for your search.</div>
          ) : (
            <div className="ctm-list">
              {shown.map((t) => (
                <div
                  className={`ctm-row${t.active ? '' : ' ctm-inactive'}`}
                  key={t.id}
                  draggable
                  onDragStart={(e) => {
                    dragKey.current = t.id;
                    e.currentTarget.classList.add('ctm-dragging');
                  }}
                  onDragEnd={(e) => e.currentTarget.classList.remove('ctm-dragging')}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('ctm-drop-before');
                  }}
                  onDragLeave={(e) => e.currentTarget.classList.remove('ctm-drop-before')}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('ctm-drop-before');
                    reorder(t.id);
                  }}
                >
                  <span className="ctm-handle" aria-hidden title="Drag to reorder">
                    {HANDLE}
                  </span>
                  <span className="ctm-num" />
                  <div className="ctm-name">
                    <span className="ctm-name-text">{t.name || '(unnamed)'}</span>
                    {t.system && <span className="ctm-tag ctm-tag-sys">system</span>}
                    {!t.active && <span className="ctm-tag ctm-tag-off">inactive</span>}
                    {t.fieldCount > 0 && (
                      <span className="ctm-tag ctm-tag-fields">
                        {t.fieldCount} field{t.fieldCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className="ctm-acts">
                    <button
                      type="button"
                      className={`ctm-switch ${t.active ? 'on' : 'off'}`}
                      role="switch"
                      aria-checked={t.active}
                      title={
                        t.active ? 'Active — click to deactivate' : 'Inactive — click to activate'
                      }
                      onClick={() => toggleActive(t)}
                    >
                      <span className="ctm-switch-knob" />
                    </button>
                    <button
                      className="ctm-icon"
                      title="Edit fields"
                      aria-label={`Edit ${t.name} fields`}
                      onClick={() => setEditType(t)}
                    >
                      {Pencil}
                    </button>
                    <button
                      className="ctm-icon danger"
                      title="Delete"
                      aria-label={`Delete ${t.name}`}
                      onClick={() => remove(t)}
                    >
                      {Xmark}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {editType && (
        <ContactTypeFieldForm
          type={editType}
          onClose={() => {
            setEditType(null);
            load();
          }}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}
